import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, Loader2, RotateCcw, Check, Pencil } from "lucide-react";
import { apiClient } from "@/api/client";
import { formatApiError } from "@/utils/httpErrors";
import { toast } from "sonner";
import { VERTICALS } from "@/lib/verticals";

/**
 * DOC-INTAKE-1 — CPA configuration of the seller document request list.
 *
 * The required set resolves in three layers server-side:
 *   catalogue default -> industry overlay (deal vertical) -> CPA override.
 * Each row reports which layer decided it via `source`, so the CPA can see
 * *why* a document is being asked for before overriding it. Toggling writes
 * only the delta; clearing an override lets the lower layers speak again.
 */

interface RequestRow {
  key: string;
  label: string;
  description: string;
  required: boolean;
  source: "default" | "industry" | "cpa";
}

interface ConfigResponse {
  deal_id: string;
  vertical: string | null;
  overrides: Record<string, boolean>;
  requests: RequestRow[];
}

interface Props {
  dealId?: string;
}

const SOURCE_LABEL: Record<RequestRow["source"], string> = {
  default: "Standard",
  industry: "Industry",
  cpa: "Your override",
};

export const DocumentRequestConfig: React.FC<Props> = ({ dealId }) => {
  const [rows, setRows] = useState<RequestRow[]>([]);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [vertical, setVertical] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // VERTICAL-EDITOR-UNREACHABLE-1 (QOE-DEV-BATCH-2 WP-4) — the only inline
  // vertical editor lived in IndustryBenchmarkCard, which does not render
  // until the executive summary is complete. That's invisible on a brand-new
  // deal — exactly when a CPA most needs to fix a wrong/default vertical,
  // before the wrong document request list goes out to the seller. This
  // panel already renders `vertical` and already renders on day one, so the
  // same edit-in-place capability is surfaced here too (placement, not new
  // logic — modeled directly on IndustryBenchmarkCard's editor).
  const [editingVertical, setEditingVertical] = useState(false);
  const [pendingVertical, setPendingVertical] = useState<string>("");
  const [savingVertical, setSavingVertical] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const applyConfig = (data: ConfigResponse) => {
    setRows(data.requests ?? []);
    setOverrides(data.overrides ?? {});
    setVertical(data.vertical ?? null);
  };

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<ConfigResponse>(`/qoe/deals/${dealId}/document-requests`);
        if (!cancelled) applyConfig(res.data);
      } catch (err: unknown) {
        if (!cancelled) setError(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dealId, refreshKey]);

  const startEditingVertical = () => {
    setPendingVertical(vertical || VERTICALS[VERTICALS.length - 1]);
    setEditingVertical(true);
  };

  const saveVertical = async () => {
    if (!dealId || !pendingVertical) return;
    setSavingVertical(true);
    try {
      await apiClient.patch(`/qoe/deals/${dealId}`, { vertical: pendingVertical });
      setEditingVertical(false);
      setRefreshKey((k) => k + 1);
      toast.success("Industry vertical updated.");
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(
        status === 422
          ? formatApiError(err)
          : "The industry vertical could not be saved. Please try again in a moment."
      );
    } finally {
      setSavingVertical(false);
    }
  };

  const persist = async (next: Record<string, boolean>) => {
    if (!dealId) return;
    setSaving(true);
    setError(null);
    const prevRows = rows;
    const prevOverrides = overrides;
    try {
      const res = await apiClient.put<ConfigResponse>(
        `/qoe/deals/${dealId}/document-requests`,
        { overrides: next }
      );
      applyConfig(res.data);
      setSavedAt(Date.now());
    } catch (err: unknown) {
      // Roll back the optimistic state — a 503 during SQLite fallback must not
      // leave the CPA believing a change stuck when it did not.
      setRows(prevRows);
      setOverrides(prevOverrides);
      const status = (err as { response?: { status?: number } }).response?.status;
      setError(
        status === 503
          ? "The database is temporarily read-only. Your change was not saved — try again shortly."
          : "Could not save your change. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggle = (row: RequestRow) => {
    const next = { ...overrides, [row.key]: !row.required };
    setRows((r) =>
      r.map((x) => (x.key === row.key ? { ...x, required: !row.required, source: "cpa" } : x))
    );
    setOverrides(next);
    persist(next);
  };

  const clearOverride = (key: string) => {
    const next = { ...overrides };
    delete next[key];
    persist(next);
  };

  const resetAll = () => persist({});

  if (!dealId) return null;

  const requiredCount = rows.filter((r) => r.required).length;
  const overrideCount = Object.keys(overrides).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <CardTitle className="text-base">Requested Documents</CardTitle>
            {editingVertical ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">Industry:</span>
                <Select value={pendingVertical} onValueChange={(v) => v && setPendingVertical(v)}>
                  <SelectTrigger className="h-8 w-full max-w-xs bg-white sm:w-auto">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VERTICALS.map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={saveVertical}
                  disabled={savingVertical}
                  className="rounded bg-[var(--color-primary,#0d1f3c)] px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                >
                  {savingVertical ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingVertical(false)}
                  disabled={savingVertical}
                  className="rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <CardDescription className="flex flex-wrap items-center gap-2">
                What the seller is asked to upload.{" "}
                {vertical ? (
                  <>
                    Tailored for <span className="font-medium">{vertical}</span>.
                  </>
                ) : (
                  <>No industry set on this deal — standard list applies.</>
                )}
                <button
                  type="button"
                  onClick={startEditingVertical}
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
                >
                  <Pencil className="h-3 w-3" />
                  {vertical ? "Change" : "Set industry"}
                </button>
              </CardDescription>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            {!saving && savedAt && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Check className="h-3 w-3" /> Saved
              </span>
            )}
            {overrideCount > 0 && (
              <Button variant="outline" size="sm" onClick={resetAll} disabled={saving}>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {error && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="min-w-0">{error}</span>
              </div>
            )}

            <p className="text-xs text-muted-foreground mb-3">
              {requiredCount} of {rows.length} marked required
              {overrideCount > 0 && ` · ${overrideCount} customised`}
            </p>

            <ul className="divide-y">
              {rows.map((row) => (
                <li
                  key={row.key}
                  className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
                >
                  <label
                    htmlFor={`docreq-${row.key}`}
                    className="flex items-start gap-3 min-w-0 cursor-pointer"
                  >
                    <input
                      id={`docreq-${row.key}`}
                      type="checkbox"
                      checked={row.required}
                      disabled={saving}
                      onChange={() => toggle(row)}
                      className="mt-1 h-4 w-4 shrink-0 rounded border-input accent-primary"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium break-words">{row.label}</span>
                      <span className="block text-xs text-muted-foreground break-words">
                        {row.description}
                      </span>
                    </span>
                  </label>

                  <div className="flex items-center gap-2 shrink-0 pl-7 sm:pl-0">
                    <Badge variant={row.source === "cpa" ? "default" : "secondary"}>
                      {SOURCE_LABEL[row.source]}
                    </Badge>
                    {row.source === "cpa" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        disabled={saving}
                        onClick={() => clearOverride(row.key)}
                      >
                        Undo
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <p className="mt-4 text-xs text-muted-foreground">
              Changes apply to the seller portal immediately. Unchecking a document keeps it
              available to upload — it just stops counting toward the seller's progress.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentRequestConfig;
